import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  FileSearch, Calculator, FileText, User, Clock, MapPin, Database,
  ArrowRight, BookOpen, AlertTriangle, Upload, Sparkles, PencilLine, ScanLine,
  ExternalLink, Download, History,
} from "lucide-react";
import { toast } from "sonner";

const METHOD_ICON = {
  "Manual entry": PencilLine,
  "Bulk CSV / XLSX upload": Upload,
  "AI Header Mapper": Sparkles,
  "Document extraction (OCR + AI vision)": ScanLine,
};

export const AUDIT_ACTION_LABEL = {
  create: "Created", edit: "Edited", reassign_facility: "Facility reassigned",
  factor_change: "Factor changed", period_edit: "Period edited",
  status_change: "Status changed", delete: "Deleted",
};
const AUDIT_ACTION_TONE = {
  create: "success", edit: "outline", reassign_facility: "warning",
  factor_change: "warning", period_edit: "warning", status_change: "default", delete: "danger",
};

const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

export function AuditTrail({ source, recordId }) {
  const [state, setState] = useState({ entries: [], total: 0, page: 0, pages: 1, loading: true });
  const loadPage = async (page) => {
    try {
      const r = await api.get("/esg-records/audit", { params: { source, record_id: recordId, page, page_size: 8 } });
      setState((s) => ({
        entries: page === 1 ? r.data.entries : [...s.entries, ...r.data.entries],
        total: r.data.total, page: r.data.page, pages: r.data.pages, loading: false,
      }));
    } catch (e) { setState((s) => ({ ...s, loading: false })); }
  };
  useEffect(() => { setState({ entries: [], total: 0, page: 0, pages: 1, loading: true }); loadPage(1); /* eslint-disable-next-line */ }, [source, recordId]);
  return (
    <div className="card-flat p-3" data-testid="provenance-audit">
      <div className="label-eyebrow inline-flex items-center gap-1 mb-2"><History className="h-3.5 w-3.5 text-accent" /> Audit trail — every change <span className="text-muted-foreground font-mono normal-case tracking-normal" data-testid="audit-total">({state.total})</span></div>
      {state.loading ? <div className="text-xs text-muted-foreground py-2">Loading change history…</div>
        : state.entries.length === 0 ? <div className="text-xs text-muted-foreground py-2" data-testid="audit-empty">No changes recorded yet. Any future edit, reassignment, factor change or verification will be logged here.</div> : (
        <div className="relative pl-3 space-y-2.5 border-l border-border">
          {state.entries.map((e) => (
            <div key={e.id} className="relative" data-testid="audit-entry">
              <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full bg-accent" />
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={AUDIT_ACTION_TONE[e.action] || "outline"} className="text-[9px]">{AUDIT_ACTION_LABEL[e.action] || e.action}</Badge>
                <span className="text-[11px] text-muted-foreground font-mono">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                <span className="text-[11px]">· {e.actor}{e.role ? ` (${e.role})` : ""}</span>
              </div>
              {(e.changes || []).map((c, i) => (
                <div key={i} className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid="audit-change">
                  <span className="text-foreground/80">{c.field}</span>
                  <span className="font-mono line-through opacity-70">{fmtVal(c.old)}</span>
                  <ArrowRight className="h-2.5 w-2.5" />
                  <span className="font-mono text-foreground">{fmtVal(c.new)}</span>
                </div>
              ))}
              {e.detail && <div className="text-[10px] text-muted-foreground/80 mt-0.5 italic">{e.detail}</div>}
            </div>
          ))}
          {state.page < state.pages && (
            <Button variant="outline" size="sm" className="mt-1 h-7 text-[11px]" onClick={() => loadPage(state.page + 1)} data-testid="audit-load-more">Load more ({state.total - state.entries.length} older)</Button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 flex items-baseline justify-between gap-3">
        <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground shrink-0">{label}</span>
        <span className="text-sm text-right break-words">{value}</span>
      </div>
    </div>
  );
}

export function ProvenanceDialog({ pointId, open, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !pointId) return;
    setData(null); setErr(null);
    api.get(`/data/${pointId}/provenance`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  }, [open, pointId]);

  const openEvidence = () => {
    if (!data?.linked_document) return;
    onClose();
    navigate(`/documents?doc=${data.linked_document.id}`);
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/data/${pointId}/lineage.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `lineage_${pointId}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="provenance-dialog">
        {err ? <div className="py-10 text-center text-red-400 text-sm">{err}</div>
          : !data ? <div className="py-10 text-center text-muted-foreground text-sm">Loading lineage…</div> : (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-3 pr-6">
                <DialogTitle className="font-display flex items-center gap-2">
                  <FileSearch className="h-5 w-5 text-accent" /> Data point lineage
                </DialogTitle>
                <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="lineage-pdf-btn"><Download className="h-3.5 w-3.5" /> PDF</Button>
              </div>
            </DialogHeader>

            {/* Summary */}
            <div className="card-flat p-3 grid grid-cols-2 gap-x-4 gap-y-1">
              <div className="col-span-2 flex items-center justify-between">
                <div className="font-display font-semibold">{data.data_point.activity_type}</div>
                <Badge variant="outline" className="text-[10px]">S{data.data_point.scope} · {data.data_point.category}</Badge>
              </div>
              <div className="text-sm"><span className="text-muted-foreground text-xs">Period </span>{data.data_point.period}</div>
              <div className="text-sm text-right"><span className="text-muted-foreground text-xs">Value </span><span className="font-mono">{formatNum(data.data_point.value)} {data.data_point.unit}</span></div>
              <div className="col-span-2 text-sm"><span className="text-muted-foreground text-xs">Emissions </span><span className="font-mono font-semibold text-accent">{formatNum(data.data_point.co2e_tonnes, { decimals: 4 })} tCO₂e</span></div>
            </div>

            {/* Provenance */}
            <div className="card-flat p-3" data-testid="provenance-capture">
              <div className="label-eyebrow inline-flex items-center gap-1 mb-2"><Database className="h-3.5 w-3.5 text-accent" /> Provenance — how it was captured</div>
              {(() => {
                const MI = METHOD_ICON[data.provenance.capture_method] || PencilLine;
                return (
                  <div className="flex items-start gap-2.5 mb-2 rounded-sm bg-surface-hover/50 border border-border p-2.5">
                    <MI className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-medium" data-testid="provenance-method">{data.provenance.capture_method}</div>
                      <div className="text-[11px] text-muted-foreground">{data.provenance.capture_method_detail}</div>
                    </div>
                  </div>
                );
              })()}
              <Row icon={FileText} label="Source / file" value={data.provenance.source_file || "—"} testid="provenance-source" />
              <Row icon={User} label="Captured by" value={data.provenance.captured_by} />
              <Row icon={Clock} label="Date & time" value={data.provenance.captured_at ? new Date(data.provenance.captured_at).toLocaleString() : "—"} testid="provenance-datetime" />
              <Row icon={MapPin} label="Facility" value={data.provenance.facility} />
            </div>

            {/* Linked evidence document */}
            {data.linked_document && (
              <div className="card-flat p-3" data-testid="provenance-document">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-eyebrow inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5 text-accent" /> Supporting evidence document</div>
                  <Button variant="ghost" size="sm" className="h-7 text-[11px] text-accent" onClick={openEvidence} data-testid="open-evidence-btn"><ExternalLink className="h-3 w-3" /> Open evidence</Button>
                </div>
                <Row icon={FileText} label="File" value={data.linked_document.filename} />
                <Row icon={Database} label="Type / size" value={`${data.linked_document.mime || "—"} · ${data.linked_document.size_bytes ? (data.linked_document.size_bytes / 1024).toFixed(0) + " KB" : "—"}`} />
                <Row icon={User} label="Uploaded by" value={data.linked_document.uploaded_by} />
                <Row icon={Clock} label="Uploaded at" value={data.linked_document.uploaded_at ? new Date(data.linked_document.uploaded_at).toLocaleString() : "—"} />
                <div className="mt-1"><Badge variant={data.linked_document.status === "confirmed" ? "success" : "outline"} className="text-[9px]">{data.linked_document.status}</Badge></div>
              </div>
            )}

            {/* Calculation lineage */}
            <div className="card-flat p-3" data-testid="provenance-calculation">
              <div className="label-eyebrow inline-flex items-center gap-1 mb-2"><Calculator className="h-3.5 w-3.5 text-accent" /> Calculation — how it was computed</div>
              {data.calculation.calculated ? (
                <>
                  <div className="font-mono text-xs bg-surface-hover/50 border border-border rounded-sm p-2 mb-3" data-testid="provenance-formula">{data.calculation.formula}</div>
                  <div className="space-y-2 mb-3">
                    {data.calculation.steps.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-[10px] font-mono text-muted-foreground w-4">{i + 1}</span>
                        <span className="font-mono text-xs">{s.expression}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-mono text-xs font-semibold text-accent">{s.result}</span>
                      </div>
                    ))}
                  </div>
                  {/* emission factor source */}
                  <div className="rounded-sm border border-accent/20 bg-accent/5 p-2.5" data-testid="provenance-factor">
                    <div className="flex items-center gap-1.5 mb-1.5"><BookOpen className="h-3.5 w-3.5 text-accent" /><span className="text-xs font-semibold">Emission factor &amp; source</span>
                      {data.calculation.basis === "spend_based" && <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-[#FFB800]/20 text-[#8a5a00] dark:text-[#FFB800]" data-testid="provenance-spend-badge">Spend-based · lower quality</span>}</div>
                    <div className="text-sm font-medium">{data.calculation.emission_factor.name} <span className="font-mono text-xs text-muted-foreground">({data.calculation.emission_factor.code})</span></div>
                    <div className="font-mono text-xs text-muted-foreground mt-0.5">{data.calculation.emission_factor.factor_kgco2e} kgCO₂e / {data.calculation.emission_factor.unit}</div>
                    {data.calculation.data_quality && <div className="text-[11px] text-[#8a5a00] dark:text-[#FFB800] mt-1">{data.calculation.data_quality}</div>}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2 text-[11px]">
                      <div><span className="text-muted-foreground">Source: </span>{data.calculation.emission_factor.source}</div>
                      <div><span className="text-muted-foreground">Issuing body: </span>{data.calculation.emission_factor.issuing_body || "—"}</div>
                      <div><span className="text-muted-foreground">Region: </span>{data.calculation.emission_factor.region || "—"}</div>
                      <div><span className="text-muted-foreground">Year: </span>{data.calculation.emission_factor.year || "—"}</div>
                      <div><span className="text-muted-foreground">IPCC version: </span>{data.calculation.emission_factor.ipcc_version || "—"}</div>
                      <div><span className="text-muted-foreground">Registry: </span>{data.calculation.emission_factor.registry}</div>
                    </div>
                    {data.data_point.factor_vintage_note && (
                      <div className="mt-2 flex items-start gap-1.5 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 p-2 text-[11px] text-[#FFB800]" data-testid="provenance-vintage-note">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /><span>{data.data_point.factor_vintage_note}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="provenance-no-calc">{data.calculation.note}</p>
              )}
            </div>

            {/* Anomaly */}
            {data.anomaly && (
              <div className="card-flat p-3 border-red-500/30" data-testid="provenance-anomaly">
                <div className="label-eyebrow inline-flex items-center gap-1 mb-2 text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> Anomaly flag</div>
                <p className="text-sm">{data.anomaly.note}</p>
                {data.anomaly.deviation_pct != null && (
                  <div className="text-[11px] text-muted-foreground mt-1">Deviation {data.anomaly.deviation_pct}% from baseline {formatNum(data.anomaly.baseline)} · severity {data.anomaly.severity}</div>
                )}
              </div>
            )}

            {/* Immutable audit trail (RD-17) */}
            <AuditTrail source="data_points" recordId={pointId} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
